<template>
  <div class="json-builder-action-wrapper">
    <div v-if="loading" class="loading">Loading...</div>
    <div v-if="!hideMessageTypeSelector" class="actions-container" :class="{ disabled: finalJsonElements.length }">
      <JsonBuildActions :selectedAction="messageType" :editBroadcast="editBroadcast" :isViewOnlyMode="isViewOnlyMode" :isEditMode="isEditMode" :isGoogleRcsCompliance="isGoogleRcsCompliance" @action="actionHandler" ref="JsonBuildActions"></JsonBuildActions>
    </div>
    <div>
      <JsonBuilder :editBroadcast="editBroadcast" :isViewOnlyMode="isViewOnlyMode" :enableURLUpload="enableURLUpload" :uploaded_url_type="uploaded_url_type" :removeButton="true" :cancelButton="true" :disableTranscode="disableTranscode" :isSmsCompliance="isSmsCompliance" @loading="updateLoadingState" @finalJsonElements="onFinalJsonElementsUpdate" @removeContent="onRemoveContent" @finalCarouselData="transformCarouselData" @url-type="(value) => { urlType = value; }" ref="JsonBuilderReference"></JsonBuilder>
    </div>
  </div>
</template>

<script>
import JsonBuilder from "./json-builder.vue";
import JsonBuildActions from "./json-builder-actions.vue";
export default {
  components: { JsonBuilder, JsonBuildActions },
  emits: ["finalJson", "finalJsonElements", "carouselData", "final-url-type"],
  data() {
    return {
      loading: false,
      finalJsonElements: [],
      messageType: "",
      urlType: 'static',
      autoInitialized: false
    }
  },
  computed: {
    shouldAutoInitialize() {
      return this.hideMessageTypeSelector && this.isSmsCompliance && this.editBroadcast?.channel === 'sms' && !this.editBroadcast?.id;
    }
  },
  props: {
    editBroadcast: {
      type: Object
    },
    isViewOnlyMode: {
      type: Boolean,
      default: false
    },
    enableURLUpload: {
      type: Boolean,
      default: false
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    uploaded_url_type: {
      type: String,
      default: 'static'
    },
    hideMessageTypeSelector: {
      type: Boolean,
      default: false
    },
    disableTranscode: {
      type: Boolean,
      default: false
    },
    isSmsCompliance: {
      type: Boolean,
      default: false
    },
    isGoogleRcsCompliance: {
      type: Boolean,
      default: false
    }
  },
   watch: {
    urlType(newVal) {
      this.$emit('final-url-type', newVal);
    },
  },
  mounted() {
    // Auto-add a single text element for SMS compliance
    if (this.shouldAutoInitialize && !this.autoInitialized) {
      this.autoInitialized = true;
      this.$nextTick(() => {
        const ref = this.$refs.JsonBuilderReference;
        if (ref && this.finalJsonElements.length === 0) {
          this.messageType = 'AddText';
          ref.resetToSingleText();
        }
      });
    }
    
    // For Google RCS compliance, check if we should initialize to "none"
    if (this.isGoogleRcsCompliance) {
      this.$nextTick(() => {
        // If editBroadcast has no answers or empty message, set to "none"
        const hasNoContent = !this.editBroadcast?.answers || 
          this.editBroadcast.answers.length === 0 ||
          (this.editBroadcast.answers.length === 1 && this.isEmptyMessage(this.editBroadcast.answers[0]));
          
        if (hasNoContent) {
          this.setMessageTypeToNone();
        }
      });
    }
  },
  methods: {
    onRemoveContent() {
      this.$refs.JsonBuildActions.optionSelected = undefined;
    },
    actionHandler({ actionName }) {
      // Adding a timeout for the JsonBuilderReference to be available
      setTimeout(() => {
        const { JsonBuilderReference } = this.$refs;
        if (!JsonBuilderReference) {
          console.error('JsonBuilderReference is still undefined after the timeout.');
          return;
        }
        
        // For Google RCS compliance, clear previous content when changing message type
        if (this.isGoogleRcsCompliance && this.finalJsonElements.length > 0) {
          JsonBuilderReference.clearAllItems();
        }
        
        this.messageType = actionName;
        
        // Handle "none" action - clear all content
        if (actionName === 'none') {
          if (JsonBuilderReference) {
            JsonBuilderReference.clearAllItems();
          }
          this.finalJsonElements = [];
          this.$emit('finalJsonElements', { finalJsonElements: [] });
          return;
        }
        
        switch (actionName) {
          case 'AddText':
            JsonBuilderReference.actions().addText();
            break;
          case 'AddImage':
            JsonBuilderReference.actions().addImage();
            break;
          case 'AddVideo':
            JsonBuilderReference.actions().addVideo();
            break;
          case 'addDocument':
            JsonBuilderReference.actions().addDocument();
            break;
          case 'AddQuickReplies':
            JsonBuilderReference.actions().addQuickReplies();
            break;
          case 'carouselRcs':
            JsonBuilderReference.actions().addCarouselRcs();
            break;
          case 'richcardRcs':
            JsonBuilderReference.actions().addRichCardRcs();
            break;
          default:
            console.warn('Unknown actionName:', actionName);
        }
      }, 100);
    },
    onFinalJsonUpdate({ finalJson }) {
      this.$emit('finalJson', { finalJson })
    },
    onFinalJsonElementsUpdate({finalJsonElements}) {
      this.finalJsonElements = finalJsonElements;
      
      // If no elements and this is Google RCS compliance, set to "none"
      if (this.isGoogleRcsCompliance && (!finalJsonElements || finalJsonElements.length === 0)) {
        this.messageType = 'none';
        if (this.$refs.JsonBuildActions) {
          this.$refs.JsonBuildActions.optionSelected = 'none';
        }
        this.$emit('finalJsonElements', { finalJsonElements: [] });
        return;
      }
      
      // Set message type based on element type
      if (finalJsonElements && finalJsonElements.length > 0) {
        switch (finalJsonElements[0]?.type) {
          case 'text':
            this.messageType = 'AddText'
            break;
          case 'image':
            this.messageType = 'AddImage'
            break;
          case 'video':
            this.messageType = 'AddVideo'
            break;
          case 'document':
            this.messageType = 'addDocument'
            break;
          case 'reply':
            this.messageType = 'AddQuickReplies'
            break;
          case 'carousel':
          case 'carouselRcs':
            this.messageType = 'carouselRcs';
            break;
          case 'richcard':
          case 'richcardRcs':
            this.messageType = 'richcardRcs';
            break;
          default:
            break;
        }
      }
      this.$emit('finalJsonElements', { finalJsonElements })
    },
    updateLoadingState({ loading }) {
      this.loading = loading;
    },
    isEmptyMessage(message) {
      if(message?.template?.elements?.length) return false;
      if (!message) return true;
      if (typeof message === 'object' && Object.keys(message).length === 0) return true;
      if (Array.isArray(message?.message) && message.message.length === 0) return true;
      if (typeof message?.message === 'string' && message.message.trim() === '') return true;
      return false;
    },
    setMessageTypeToNone() {
      // Set the message type to "none" and update the dropdown
      this.messageType = 'none';
      this.finalJsonElements = [];
      if (this.$refs.JsonBuildActions) {
        this.$refs.JsonBuildActions.optionSelected = 'none';
      }
    },
    clearElements() {
      const ref = this.$refs.JsonBuilderReference;
      if (!ref) return;
      // For SMS compliance, atomically reset to a single text box so repeated
      if (this.shouldAutoInitialize) {
        this.messageType = 'AddText';
        ref.resetToSingleText();
      } else {
        ref.clearAllItems();
      }
    },
    transformCarouselData({finalCarouselData}) {
      this.$emit('carouselData',  finalCarouselData );
    }
  }
}
</script>

<style scoped>
.json-builder-action-wrapper {
  position: relative;
}
.actions-container.disabled {
  pointer-events: none;
  opacity: 0.5;
}
.loading {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 999;
  background: var(--black);
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>